import {
  createTheme,
  type MantineColor,
  type MantineThemeOverride,
} from '@mantine/core'

import {
  colors,
  spacing,
  shadows,
  radius,
  breakpoints,
  headings,
  lineHeights,
  fontFamily,
  fontSizes,
} from './tokens'

/**
 * Return a theme used on MantineProvider.
 *
 * @see https://v6.mantine.dev/theming/theme-object/
 * @returns MantineThemeOverride
 */
export const useMantineTheme = (
  themeOverride?: MantineThemeOverride
): MantineThemeOverride => {
  const themePrimaryColor = (themeOverride?.primaryColor ??
    'brand') as MantineColor

  const themePrimaryShade = (themeOverride?.primaryShade ??
    7) as MantineThemeOverride['primaryShade']

  const themeFontFamily = themeOverride?.fontFamily ?? fontFamily

  const theme = createTheme({
    colors,
    radius,
    spacing,
    shadows,
    breakpoints,
    fontFamily: themeFontFamily,
    fontSizes,
    lineHeights,

    cursorType: 'pointer',
    defaultRadius: 'md',
    primaryColor: themePrimaryColor,
    primaryShade: themePrimaryShade,

    headings: {
      fontFamily: themeFontFamily,
      fontWeight: '500',
      sizes: {
        h1: { fontSize: headings.h1, lineHeight: lineHeights.md },
        h2: { fontSize: headings.h2, lineHeight: lineHeights.md },
        h3: { fontSize: headings.h3, lineHeight: lineHeights.md },
        h4: { fontSize: headings.h4, lineHeight: lineHeights.md },
        h5: { fontSize: headings.h5, lineHeight: lineHeights.md },
        h6: { fontSize: headings.h6, lineHeight: lineHeights.md },
      },
    },

    components: {
      Avatar: {
        defaultProps: {
          radius: 'sm',
        },
      },
      Alert: {
        defaultProps: {
          py: 'md',
        },
      },
      Button: {
        defaultProps: {
          size: 'md',
          variant: 'filled',
          radius: 'sm',
          px: 'md',
          fs: 'sm',
          fw: 500,
        },
        styles: {
          ':root': {
            fontWeight: 'normal',
          },
        },
      },
      Anchor: {
        defaultProps: {
          c: 'blue.6',
        },
      },
      ActionIcon: {
        defaultProps: {
          radius: 'sm',
          size: 'md',
          variant: 'subtle',
          color: 'gray',
        },
      },
      Input: {
        defaultProps: {
          size: 'md',
          radius: 'sm',
          inputwrapperorder: ['label', 'input', 'description', 'error'],
        },
      },
      Select: {
        defaultProps: {
          size: 'md',
          checkIconPosition: 'right',
          inputwrapperorder: ['label', 'input', 'description', 'error'],
        },
      },
      NumberInput: {
        defaultProps: {
          size: 'md',
          inputwrapperorder: ['label', 'input', 'description', 'error'],
        },
      },
      Textarea: {
        defaultProps: {
          size: 'md',
          inputwrapperorder: ['label', 'input', 'description', 'error'],
        },
      },
      TextInput: {
        defaultProps: {
          size: 'md',
          inputwrapperorder: ['label', 'input', 'description', 'error'],
        },
      },
      PasswordInput: {
        defaultProps: { size: 'md' },
      },
      Text: {
        defaultProps: {
          component: 'div',
        },
      },
      Tabs: {
        defaultProps: { size: 'md', radius: 'xs' },
      },
      Tooltip: {
        defaultProps: {
          px: 'md',
          py: 'xs',
          radius: 'sm',
          withArrow: false,
          arrowSize: 6,
          events: { hover: true, focus: true, touch: true },
        },
      },
      Badge: {
        defaultProps: {
          size: 'lg',
          variant: 'light',
        },
      },
      Checkbox: {
        defaultProps: {
          radius: 'xs',
        },
      },
      MultiSelect: {
        defaultProps: {
          size: 'md',
          checkIconPosition: 'right',
        },
      },
      Autocomplete: {
        defaultProps: {
          size: 'md',
        },
      },
      DateInput: {
        defaultProps: {
          size: 'md',
        },
      },
      Popover: {
        defaultProps: {
          withinPortal: true,
        },
      },
      Card: {
        defaultProps: {
          p: 'lg',
        },
      },
    },
  })

  return theme
}
